import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CreditCard,
  History,
  RefreshCcw,
  Sparkles,
  Receipt,
  Loader2,
  ArrowRight,
  Sun,
  Moon,
} from "lucide-react";
import ChaosHeader from "@/components/ChaosHeader";
import AppFooter from "@/components/AppFooter";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { ScanAPI, ProfileAPI, BillingAPI } from "@/services/api";

const VERDICT_TONE = {
  true: "#22c55e",
  mostly_true: "#84cc16",
  mixed: "#f59e0b",
  mostly_false: "#fb7185",
  false: "#FF0033",
  unverifiable: "#94a3b8",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const PLANS = [
  {
    id: "starter_bundle",
    name: "Starter Bundle",
    engine: "Gemini Light",
    price: "$10",
    cadence: "/ one-time",
    credits: "+10 credits",
    blurb:
      "AI: Gemini Light (basic Google database search). Background Checks included. Top up exactly when you need a verification burst.",
  },
  {
    id: "pro_bundle",
    name: "Pro Bundle",
    engine: "Gemini Light",
    price: "$19",
    cadence: "/ one-time",
    credits: "+19 credits",
    blurb:
      "AI: Gemini Light (basic Google database search). Background Checks included. Best value per scan — stack two for a heavy news week.",
  },
  {
    id: "growth_sub",
    name: "Claude Plan",
    engine: "Claude 4.5",
    price: "$25.99",
    cadence: "/ month",
    credits: "250 credits / mo",
    blurb:
      "AI: Claude 4.5 verdicts with structured analysis. Background Checks included. Auto-refills 250 credits every billing cycle.",
  },
  {
    id: "infinite_sub",
    name: "Perplexity Plan",
    engine: "Perplexity AI",
    price: "$35.99",
    cadence: "/ month",
    credits: "Unlimited credits",
    comingSoon: true,
    blurb:
      "AI: Perplexity AI deep research grade. Background Checks included. Effectively unlimited scans, auto-renewed monthly.",
  },
];

const ProfilePage = () => {
  const { user, status, setCredits } = useAuth();
  const { isLight, toggleTheme } = useTheme();
  const [scans, setScans] = useState([]);
  const [loadingScans, setLoadingScans] = useState(false);
  const [scansError, setScansError] = useState("");
  const [resetting, setResetting] = useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);
  const [billingMsg, setBillingMsg] = useState("");
  const [billingMode, setBillingMode] = useState(null); // 'test' | 'live' | null

  useEffect(() => {
    let cancelled = false;
    BillingAPI.mode()
      .then((data) => {
        if (!cancelled) setBillingMode(data?.mode || null);
      })
      .catch(() => {
        /* non-blocking */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll Stripe status if we came back with ?session_id=...
  useEffect(() => {
    if (status !== "authed") return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const billing = params.get("billing");
    if (!sessionId) {
      if (billing === "cancel") setBillingMsg("Checkout cancelled.");
      return;
    }
    setBillingMsg("Confirming payment…");
    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      attempt += 1;
      try {
        const data = await BillingAPI.status(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          if (typeof data.credits_remaining === "number") {
            setCredits(data.credits_remaining);
          }
          setBillingMsg(
            `Payment confirmed — credits topped up. New balance: ${data.credits_remaining}.`
          );
          const cleanUrl = `${window.location.origin}${window.location.pathname}`;
          window.history.replaceState({}, document.title, cleanUrl);
          return;
        }
        if (data.status === "expired") {
          setBillingMsg("Checkout session expired. Please try again.");
          return;
        }
        if (attempt >= 6) {
          setBillingMsg(
            "Still processing… Refresh in a minute to confirm your credits."
          );
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setBillingMsg("Could not verify payment yet.");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [status, setCredits]);

  useEffect(() => {
    if (status !== "authed") return;
    let cancelled = false;
    (async () => {
      setLoadingScans(true);
      setScansError("");
      try {
        const { scans: rows } = await ScanAPI.recent();
        if (!cancelled) setScans(rows || []);
      } catch (err) {
        if (!cancelled)
          setScansError(
            err?.response?.data?.detail || "Could not load your history."
          );
      } finally {
        if (!cancelled) setLoadingScans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status]);

  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      const { credits_ledger_balance } = await ProfileAPI.resetCredits();
      if (typeof credits_ledger_balance === "number") {
        setCredits(credits_ledger_balance);
      }
    } catch {
      /* surfaced via header */
    } finally {
      setResetting(false);
    }
  };

  const handleBuy = async (planId) => {
    if (checkoutPlanId) return;
    setCheckoutPlanId(planId);
    setBillingMsg("");
    try {
      const { checkout_url } = await BillingAPI.checkout(planId);
      window.location.assign(checkout_url);
    } catch (err) {
      setBillingMsg(
        err?.response?.data?.detail || "Could not start checkout. Try again."
      );
      setCheckoutPlanId(null);
    }
  };

  if (status === "loading") {
    return (
      <div className="chaos-grain chaos-bg-gradient min-h-[100dvh] flex items-center justify-center text-[#cbd5e1] font-mono text-xs uppercase tracking-[0.24em]">
        Loading…
      </div>
    );
  }

  if (status !== "authed") {
    return (
      <div className="chaos-grain chaos-bg-gradient min-h-[100dvh] w-full text-[#f1f5f9]">
        <ChaosHeader />
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 py-16 text-center">
          <h1 className="text-2xl lg:text-4xl font-extrabold uppercase tracking-tight mb-3">
            Sign in to view your <span className="text-[#FF0033]">profile</span>.
          </h1>
          <p className="font-mono text-[12px] text-[#94a3b8] mb-6">
            Your credit ledger and scan history live here.
          </p>
          <Link
            to="/"
            data-testid="profile-return-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#FF0033] hover:text-white"
          >
            <ArrowLeft size={13} /> Back to terminal
          </Link>
        </div>
        <AppFooter />
      </div>
    );
  }

  const credits = user?.credits_ledger_balance ?? 0;

  return (
    <div
      data-testid="profile-page"
      className="chaos-grain chaos-bg-gradient min-h-[100dvh] w-full text-[#f1f5f9]"
    >
      <ChaosHeader showCredits />

      <main className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-8 lg:pt-12 pb-20">
        {/* Eyebrow */}
        <div className="flex items-center gap-2.5 mb-5 chaos-reveal">
          <span aria-hidden className="inline-block w-1.5 h-1.5 bg-[#FF0033]" />
          <span className="font-mono text-[10px] lg:text-xs uppercase tracking-[0.28em] text-[#94a3b8]">
            /operator-profile
          </span>
          <Link
            to="/"
            data-testid="back-to-terminal"
            className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#94a3b8] hover:text-[#FF0033] transition-colors"
          >
            <ArrowLeft size={12} />
            Back to terminal
          </Link>
        </div>

        <h1 className="chaos-reveal text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight uppercase leading-[0.98] mb-2">
          My profile <span className="text-[#FF0033]">/ billing</span>
        </h1>
        <p className="chaos-reveal font-mono text-xs sm:text-sm text-[#94a3b8] mb-10 max-w-2xl">
          {user?.email}
        </p>

        {/* Ledger */}
        <section
          data-testid="ledger-section"
          className="border border-white/[0.07] bg-white/[0.015] mb-10"
        >
          <div className="flex items-center gap-2.5 px-5 lg:px-6 py-3 border-b border-white/[0.06]">
            <Sparkles size={13} className="text-[#FF0033]" />
            <h2 className="font-mono text-[10px] lg:text-xs uppercase tracking-[0.24em] text-[#cbd5e1]">
              Credit ledger
            </h2>
          </div>
          <div className="px-5 lg:px-6 py-6 flex flex-col sm:flex-row sm:items-end justify-between gap-5">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#94a3b8] mb-2">
                Current credit balance
              </p>
              <p
                data-testid="profile-credit-balance"
                className="text-5xl lg:text-6xl font-extrabold text-[#f1f5f9] tabular-nums leading-none"
              >
                <span className="text-[#FF0033]">{credits}</span>
              </p>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#94a3b8]">
                2 credits = 1 live scan + {(() => {
                  if (user?.plan === "infinite_sub" || user?.is_unlimited) return "Perplexity";
                  if (user?.plan === "growth_sub") return "Claude";
                  return "Gemini";
                })()} verdict
              </p>
            </div>
            {(user?.email || "").trim().toLowerCase() === "rmctech101@gmail.com" && (
              <button
                type="button"
                data-testid="reset-credits-button"
                onClick={handleReset}
                disabled={resetting}
                className="inline-flex items-center gap-2 border border-[#FF0033] hover:bg-[#FF0033] hover:text-white text-[#FF0033] font-bold uppercase tracking-[0.18em] px-5 py-3 text-xs disabled:opacity-50 transition-colors"
              >
                {resetting ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <RefreshCcw size={13} />
                )}
                Reset credits to 5 (admin)
              </button>
            )}
          </div>
        </section>

        {/* Interface settings */}
        <section
          data-testid="settings-section"
          className="border border-white/[0.07] bg-white/[0.015] mb-10"
        >
          <div className="flex items-center gap-2.5 px-5 lg:px-6 py-3 border-b border-white/[0.06]">
            {isLight ? (
              <Sun size={13} className="text-amber-400" />
            ) : (
              <Moon size={13} className="text-[#FF0033]" />
            )}
            <h2 className="font-mono text-[10px] lg:text-xs uppercase tracking-[0.24em] text-[#cbd5e1]">
              Interface
            </h2>
          </div>
          <div className="px-5 lg:px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#94a3b8] mb-1.5">
                Workspace skin
              </p>
              <p className="text-[14px] text-[#cbd5e1]">
                Currently:{" "}
                <span
                  data-testid="current-theme-label"
                  className="font-bold text-[#FF0033] uppercase"
                >
                  {isLight ? "Light" : "Dark"}
                </span>
              </p>
            </div>
            <button
              type="button"
              data-testid="theme-toggle-button"
              onClick={toggleTheme}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF0033] hover:text-[#FF0033] text-[#cbd5e1] font-mono uppercase tracking-[0.18em] px-5 py-3 text-[11px] transition-colors"
            >
              {isLight ? <Moon size={13} /> : <Sun size={13} />}
              Toggle Interface Skin (Light/Dark)
            </button>
          </div>
        </section>

        {/* Plans */}
        <section
          data-testid="plans-section"
          className="border border-white/[0.07] bg-white/[0.015] mb-10"
        >
          <div className="flex items-center gap-2.5 px-5 lg:px-6 py-3 border-b border-white/[0.06]">
            <CreditCard size={13} className="text-[#FF0033]" />
            <h2 className="font-mono text-[10px] lg:text-xs uppercase tracking-[0.24em] text-[#cbd5e1]">
              Plans
            </h2>
            {billingMode === "test" && (
              <span
                data-testid="sandbox-mode-chip"
                title="Stripe is wired in test mode. Auto-hides when sk_live_ keys are restored."
                className="inline-flex items-center gap-1 border border-amber-400/60 bg-amber-400/10 text-amber-300 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em]"
              >
                <span className="w-1 h-1 rounded-full bg-amber-300 chaos-pulse" />
                Sandbox mode
              </span>
            )}
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">
              Stripe checkout · {billingMode === "test" ? "test" : "live"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.05]">
            {PLANS.map((p) => (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className="bg-[#020617] p-5 lg:p-6 flex flex-col"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#cbd5e1]">
                    {p.name}
                  </span>
                  {p.engine && (
                    <span
                      data-testid={`plan-engine-${p.id}`}
                      className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#94a3b8] border border-white/10 px-1.5 py-0.5"
                    >
                      {p.engine}
                    </span>
                  )}
                </div>
                <div className="mb-3">
                  <span className="text-3xl font-extrabold text-[#f1f5f9]">
                    {p.price}
                  </span>
                  <span className="font-mono text-[11px] text-[#94a3b8]">
                    {p.cadence}
                  </span>
                </div>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#FF0033] mb-2">
                  {p.credits}
                </p>
                <p className="text-[13px] text-[#94a3b8] font-mono leading-relaxed mb-5">
                  {p.blurb}
                </p>
                <button
                  type="button"
                  data-testid={`buy-${p.id}`}
                  onClick={() => !p.comingSoon && handleBuy(p.id)}
                  disabled={!!checkoutPlanId || p.comingSoon}
                  className={
                    p.comingSoon
                      ? "mt-auto w-full inline-flex items-center justify-center gap-2 border border-white/[0.12] bg-white/[0.02] text-[#94a3b8] font-bold uppercase tracking-[0.2em] px-4 py-2.5 text-[11px] cursor-not-allowed"
                      : "mt-auto w-full inline-flex items-center justify-center gap-2 border border-[#FF0033] hover:bg-[#FF0033] hover:text-white text-[#FF0033] font-bold uppercase tracking-[0.2em] px-4 py-2.5 text-[11px] disabled:opacity-50 disabled:cursor-wait transition-colors"
                  }
                >
                  {p.comingSoon ? (
                    <span data-testid={`coming-soon-${p.id}`}>Coming Soon</span>
                  ) : checkoutPlanId === p.id ? (
                    <>
                      <Loader2 size={12} className="animate-spin" /> Opening Stripe…
                    </>
                  ) : (
                    <>Buy · {p.price}</>
                  )}
                </button>
              </div>
            ))}
          </div>
          {billingMsg && (
            <div
              data-testid="billing-message"
              className="px-5 lg:px-6 py-3 border-t border-white/[0.06] font-mono text-[11px] text-[#cbd5e1]"
            >
              {billingMsg}
            </div>
          )}
          <div
            data-testid="pricing-policy"
            className="px-5 lg:px-6 py-3 border-t border-white/[0.06] font-mono text-[11px] uppercase tracking-[0.18em] text-[#cbd5e1]"
          >
            All sales are final. No refunds on credit purchases or subscriptions.
          </div>
        </section>

        {/* Purchase history */}
        <section
          data-testid="purchases-section"
          className="border border-white/[0.07] bg-white/[0.015] mb-10"
        >
          <div className="flex items-center gap-2.5 px-5 lg:px-6 py-3 border-b border-white/[0.06]">
            <Receipt size={13} className="text-[#FF0033]" />
            <h2 className="font-mono text-[10px] lg:text-xs uppercase tracking-[0.24em] text-[#cbd5e1]">
              Credit purchase history
            </h2>
          </div>
          <div className="px-5 lg:px-6 py-8 font-mono text-[12px] text-[#94a3b8] text-center">
            No purchases yet. Buy a bundle or plan above to top up.
          </div>
        </section>

        {/* Scan history */}
        <section
          id="scan-history"
          data-testid="profile-scan-history"
          className="border border-white/[0.07] bg-white/[0.015]"
        >
          <div className="flex items-center gap-2.5 px-5 lg:px-6 py-3 border-b border-white/[0.06]">
            <History size={13} className="text-[#FF0033]" />
            <h2 className="font-mono text-[10px] lg:text-xs uppercase tracking-[0.24em] text-[#cbd5e1]">
              My scan history (recent scans)
            </h2>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-[#94a3b8] tabular-nums">
              {scans.length} ROWS
            </span>
          </div>
          {loadingScans ? (
            <div className="px-5 lg:px-6 py-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#94a3b8]">
              <Loader2 size={13} className="animate-spin" />
              Loading…
            </div>
          ) : scansError ? (
            <div className="px-5 lg:px-6 py-8 font-mono text-[12px] text-[#FF0033]">
              {scansError}
            </div>
          ) : scans.length === 0 ? (
            <div className="px-5 lg:px-6 py-10 text-center font-mono text-[12px] text-[#94a3b8]">
              No scans yet — head back to the terminal and verify your first
              claim.
            </div>
          ) : (
            <ul data-testid="profile-history-list" className="divide-y divide-white/[0.05]">
              {scans.map((s) => {
                const tone = VERDICT_TONE[s.verdict?.verdict] || VERDICT_TONE.unverifiable;
                return (
                  <li
                    key={s.scan_id}
                    data-testid={`profile-history-${s.scan_id}`}
                    className="px-5 lg:px-6 py-4 hover:bg-white/[0.02] transition-colors"
                  >
                    <Link
                      to={`/?scan=${encodeURIComponent(s.scan_id)}`}
                      className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-start md:items-center gap-2 md:gap-5 group"
                    >
                      <span
                        className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold tabular-nums"
                        style={{ color: tone }}
                      >
                        {(s.verdict?.verdict || "unverifiable").replace("_", " ")}
                      </span>
                      <p className="text-[13px] lg:text-sm text-[#f1f5f9] break-words leading-snug">
                        {s.query}
                      </p>
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#94a3b8] group-hover:text-[#FF0033] transition-colors tabular-nums flex items-center gap-1.5">
                        {formatDate(s.created_at)}
                        <ArrowRight size={11} />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
      <AppFooter />
    </div>
  );
};

export default ProfilePage;
